import React, { useEffect, useState } from 'react';
import { Grid, Typography, Button, Paper, CircularProgress } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import PaymentIcon from '@material-ui/icons/Payment';
import { useHistory } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';

export default function StripeOnboardingReturn() {
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null); // { details_submitted, charges_enabled, payouts_enabled }
    const [error, setError] = useState('');

    const encryptedId = (() => {
        try { return localStorage.getItem('stripe_onboarding_encrypted_id'); } catch { return null; }
    })();

    useEffect(() => {
        if (!encryptedId) {
            setError('No onboarding session found. Please start the signup process again.');
            setLoading(false);
            return;
        }
        const checkStatus = async () => {
            try {
                const res = await fetch(`${BACKEND_URL}/account/stripe-onboarding-status/${encodeURIComponent(encryptedId)}/`);
                const data = await res.json().catch(() => ({}));
                if (res.ok) {
                    setStatus(data);
                } else {
                    setError(data.error || 'Could not retrieve onboarding status.');
                }
            } catch {
                setError('Network error checking onboarding status.');
            } finally {
                setLoading(false);
            }
        };
        checkStatus();
    }, [encryptedId]);

    const handleContinueToVerify = () => {
        if (encryptedId) {
            history.push(`/verify_user/${encryptedId}?role_type=careprovider_admin`);
        } else {
            history.push('/');
        }
    };

    return (
        <Grid container justifyContent="center" alignItems="center" style={{ minHeight: '80vh', background: 'linear-gradient(135deg, #dce4eb 0%, #d7d4cf 100%)' }}>
            <Paper style={{ padding: '48px 40px', maxWidth: 520, width: '90%', borderRadius: 16, textAlign: 'center', boxShadow: '0 8px 32px rgba(0,45,85,0.15)' }}>
                {loading ? (
                    <>
                        <CircularProgress size={48} style={{ color: '#002D55', marginBottom: 16 }} />
                        <Typography variant="h6" style={{ color: '#002D55' }}>Checking your Stripe setup...</Typography>
                    </>
                ) : error ? (
                    <>
                        <WarningIcon style={{ fontSize: 56, color: '#ff9800', marginBottom: 12 }} />
                        <Typography variant="h6" style={{ color: '#002D55', marginBottom: 8 }}>Onboarding Status</Typography>
                        <Typography variant="body1" style={{ color: '#666', marginBottom: 24 }}>{error}</Typography>
                        <Button variant="contained" onClick={() => history.push('/')}
                            style={{ background: '#002D55', color: '#fff', fontWeight: 600, borderRadius: 8 }}>
                            Return to Home
                        </Button>
                    </>
                ) : status?.details_submitted ? (
                    <>
                        <CheckCircleIcon style={{ fontSize: 64, color: '#4caf50', marginBottom: 12 }} />
                        <Typography variant="h5" style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
                            Payment Setup Complete!
                        </Typography>
                        <Typography variant="body1" style={{ color: '#555', marginBottom: 8 }}>
                            Your Stripe account has been set up successfully.
                            {status.charges_enabled && ' You can now accept payments.'}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#888', marginBottom: 24 }}>
                            Next step: verify your email to activate your clinic account.
                        </Typography>
                        <Button variant="contained" size="large" onClick={handleContinueToVerify}
                            style={{ background: '#002D55', color: '#fff', fontWeight: 600, borderRadius: 8, padding: '12px 32px' }}>
                            Continue to Email Verification
                        </Button>
                    </>
                ) : (
                    <>
                        <PaymentIcon style={{ fontSize: 56, color: '#ff9800', marginBottom: 12 }} />
                        <Typography variant="h6" style={{ color: '#002D55', fontWeight: 600, marginBottom: 8 }}>
                            Stripe Setup Incomplete
                        </Typography>
                        <Typography variant="body1" style={{ color: '#555', marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>
                            It looks like the Stripe onboarding wasn't fully completed. You can finish it later from your clinic dashboard, or continue to activate your account now.
                        </Typography>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
                            <Button variant="contained" onClick={handleContinueToVerify}
                                style={{ background: '#002D55', color: '#fff', fontWeight: 600, borderRadius: 8 }}>
                                Continue to Email Verification
                            </Button>
                        </div>
                    </>
                )}
            </Paper>
        </Grid>
    );
}
